import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { ArrowLeft, Send, PackageCheck, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  draft: "bg-anvi-100 text-anvi-700",
  issued: "bg-blue-50 text-blue-700",
  partial: "bg-amber-50 text-amber-800",
  received: "bg-emerald-50 text-emerald-800",
  closed: "bg-slate-100 text-slate-700",
};

function GRNModal({ po, existingGrns, onClose, onCreated }) {
  const received = useMemo(() => {
    const arr = po.line_items.map(() => 0);
    existingGrns.forEach((g) => g.line_items.forEach((li) => {
      if (li.po_line_index >= 0 && li.po_line_index < arr.length) arr[li.po_line_index] += Number(li.received_qty || 0);
    }));
    return arr;
  }, [po, existingGrns]);

  const [lines, setLines] = useState(po.line_items.map((_, i) => ({
    po_line_index: i, received_qty: 0, condition: "good", notes: "",
  })));
  const [receivedBy, setReceivedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const validLines = lines.filter((l) => Number(l.received_qty) > 0);
    if (validLines.length === 0) { toast.error("Enter received quantity for at least one line"); return; }
    setSaving(true);
    try {
      const res = await api.grnCreate(po.id, {
        received_by: receivedBy,
        notes,
        line_items: validLines.map((l) => ({
          po_line_index: l.po_line_index,
          received_qty: Number(l.received_qty),
          condition: l.condition,
          notes: l.notes,
        })),
      });
      toast.success(`GRN ${res.data.grn_number} recorded`);
      onCreated();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create GRN";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="create-grn-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Goods Receipt Note</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Record delivery for {po.po_number}</div>
          </div>
          <button type="button" onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Received by</label>
              <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} data-testid="grn-received-by"
                     placeholder="Site supervisor / recipient name"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">General notes</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="grn-notes"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
          </div>

          <div className="border border-anvi-200 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-anvi-50/60">
                <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">
                  <th className="px-3 py-2 text-left font-medium">Item</th>
                  <th className="px-3 py-2 text-right font-medium w-20">Ordered</th>
                  <th className="px-3 py-2 text-right font-medium w-20">Already Rcvd</th>
                  <th className="px-3 py-2 text-right font-medium w-24">Receive now</th>
                  <th className="px-3 py-2 text-left font-medium w-28">Condition</th>
                </tr>
              </thead>
              <tbody>
                {po.line_items.map((li, i) => {
                  const remaining = Math.max(0, Number(li.qty) - received[i]);
                  return (
                    <tr key={i} className="border-t border-anvi-100" data-testid={`grn-line-${i}`}>
                      <td className="px-3 py-2 text-sm text-anvi-900">
                        <div>{li.description}</div>
                        {li.spec && <div className="text-xs text-anvi-500">{li.spec}</div>}
                      </td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{li.qty} {li.unit}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums text-anvi-600">{received[i]}</td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" max={remaining} step="0.01"
                               value={lines[i].received_qty}
                               onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, received_qty: e.target.value } : x))}
                               data-testid={`grn-line-qty-${i}`}
                               className="w-full px-2 py-1 text-sm text-right tabular-nums border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
                        <div className="text-[10px] text-anvi-400 mt-0.5">Remaining: {remaining}</div>
                      </td>
                      <td className="px-3 py-2">
                        <select value={lines[i].condition}
                                onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, condition: e.target.value } : x))}
                                data-testid={`grn-line-condition-${i}`}
                                className="w-full px-2 py-1 text-sm border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                          <option value="good">good</option>
                          <option value="damaged">damaged</option>
                          <option value="partial">partial</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-anvi-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-grn"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Saving…" : "Record GRN"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [po, setPO] = useState(null);
  const [grns, setGrns] = useState([]);
  const [showGrn, setShowGrn] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const canDelete = user && (user.role === "Owner" || user.role === "Admin");

  const load = useCallback(async () => {
    try {
      const r = await api.purchaseOrder(id);
      setPO(r.data.po);
      setGrns(r.data.grns || []);
    } catch { toast.error("Could not load PO"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!po) return <div className="p-10 text-anvi-500">Loading PO…</div>;

  const issue = async () => {
    if (!window.confirm(`Issue ${po.po_number} to ${po.vendor_name}? Once issued the PO becomes non-editable.`)) return;
    setIssuing(true);
    try {
      const r = await api.purchaseOrderIssue(id);
      setPO(r.data);
      toast.success("PO issued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue PO");
    } finally { setIssuing(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${po.po_number}? This will remove any GRNs.`)) return;
    try {
      await api.purchaseOrderDelete(id);
      toast.success("PO deleted");
      navigate("/purchase-orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete PO");
    }
  };

  const received = po.line_items.map(() => 0);
  grns.forEach((g) => g.line_items.forEach((li) => {
    if (li.po_line_index >= 0 && li.po_line_index < received.length) received[li.po_line_index] += Number(li.received_qty || 0);
  }));
  const fullyReceived = po.line_items.every((li, i) => received[i] >= Number(li.qty));

  return (
    <div className="px-10 pt-4 pb-10 max-w-[1400px] animate-fade-in" data-testid="po-detail-page">
      <Link to="/purchase-orders" className="text-xs tracking-[0.15em] uppercase text-anvi-500 hover:text-anvi-terracotta flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to purchase orders
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Purchase Order</div>
          <h1 className="font-heading text-4xl text-anvi-900 font-mono">{po.po_number}</h1>
          <div className="mt-2 flex items-center gap-3">
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[po.status] || "bg-anvi-100 text-anvi-700"}`} data-testid="po-status-badge">{po.status}</span>
            <span className="text-sm text-anvi-500">Vendor: <span className="text-anvi-900">{po.vendor_name}</span></span>
            {po.expected_delivery && <span className="text-sm text-anvi-500">· Expected {po.expected_delivery}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {po.status === "draft" && (
            <button onClick={issue} disabled={issuing} data-testid="issue-po-btn"
                    className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60">
              <Send size={14} strokeWidth={1.5} /> {issuing ? "Issuing…" : "Issue PO"}
            </button>
          )}
          {po.status !== "draft" && !fullyReceived && (
            <button onClick={() => setShowGrn(true)} data-testid="new-grn-btn"
                    className="flex items-center gap-2 bg-anvi-900 hover:bg-anvi-800 text-white px-4 py-2.5 rounded-md text-sm font-medium">
              <PackageCheck size={14} strokeWidth={1.5} /> Record GRN
            </button>
          )}
          {canDelete && (po.status === "draft" || po.status === "closed") && (
            <button onClick={handleDelete} data-testid="delete-po-btn"
                    className="flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 px-3 py-2.5 rounded-md text-sm">
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-anvi-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-anvi-200 text-xs tracking-[0.15em] uppercase text-anvi-500 bg-anvi-50/60">Line Items</div>
          <table className="w-full">
            <thead>
              <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-100">
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-right font-medium w-20">Qty</th>
                <th className="px-4 py-2 text-right font-medium w-24">Received</th>
                <th className="px-4 py-2 text-right font-medium w-24">Rate</th>
                <th className="px-4 py-2 text-right font-medium w-16">GST%</th>
                <th className="px-4 py-2 text-right font-medium w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {po.line_items.map((li, i) => {
                const amt = Number(li.qty) * Number(li.rate);
                const rcv = received[i];
                const full = rcv >= Number(li.qty);
                return (
                  <tr key={i} className="border-b border-anvi-100 last:border-b-0" data-testid={`po-item-row-${i}`}>
                    <td className="px-4 py-2.5">
                      <div className="text-sm text-anvi-900">{li.description}</div>
                      {li.spec && <div className="text-xs text-anvi-500">{li.spec}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums">{li.qty} {li.unit}</td>
                    <td className={`px-4 py-2.5 text-sm text-right tabular-nums ${full ? "text-emerald-700" : rcv > 0 ? "text-amber-700" : "text-anvi-400"}`}>{rcv}</td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums text-anvi-700">{inr(li.rate)}</td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums">{li.gst_percent}%</td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums text-anvi-900 font-medium">{inr(amt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-5 py-4 border-t border-anvi-200 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-anvi-500">Subtotal</span><span className="tabular-nums text-anvi-900">{inr(po.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-anvi-500">GST</span><span className="tabular-nums text-anvi-900">{inr(po.gst_total)}</span></div>
            <div className="flex justify-between pt-2 border-t border-anvi-100">
              <span className="font-medium text-anvi-900">Grand Total</span>
              <span className="font-heading text-2xl tabular-nums text-anvi-900" data-testid="po-grand-total">{inr(po.grand_total)}</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-anvi-200 rounded-md p-5">
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500 mb-3">Terms</div>
            <div className="text-sm text-anvi-700">{po.terms || "—"}</div>
            {po.notes && (
              <>
                <div className="text-xs tracking-[0.15em] uppercase text-anvi-500 mt-4 mb-2">Notes</div>
                <div className="text-sm text-anvi-700 whitespace-pre-line">{po.notes}</div>
              </>
            )}
            {po.issued_at && (
              <div className="text-xs text-anvi-500 mt-4 pt-4 border-t border-anvi-100">
                Issued on {new Date(po.issued_at).toLocaleString("en-IN")}
              </div>
            )}
          </div>

          <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-anvi-200 flex items-center justify-between bg-anvi-50/60">
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Goods Receipt Notes</div>
              <div className="text-xs text-anvi-500">{grns.length} GRN{grns.length === 1 ? "" : "s"}</div>
            </div>
            {grns.length === 0 ? (
              <div className="p-6 text-center text-sm text-anvi-500" data-testid="no-grns">No GRNs recorded yet.</div>
            ) : (
              <div className="divide-y divide-anvi-100">
                {grns.map((g) => (
                  <div key={g.id} className="p-4" data-testid={`grn-${g.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-sm text-anvi-900">{g.grn_number}</div>
                      <div className="text-xs text-anvi-500">{g.received_at ? new Date(g.received_at).toLocaleDateString("en-IN") : ""}</div>
                    </div>
                    {g.received_by && <div className="text-xs text-anvi-600 mt-0.5">Received by {g.received_by}</div>}
                    <div className="mt-2 space-y-0.5">
                      {g.line_items.map((li, i) => (
                        <div key={i} className="text-xs text-anvi-700 flex justify-between">
                          <span>· {po.line_items[li.po_line_index]?.description || "—"}</span>
                          <span className="tabular-nums">{li.received_qty} · {li.condition}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showGrn && <GRNModal po={po} existingGrns={grns} onClose={() => setShowGrn(false)} onCreated={load} />}
    </div>
  );
}
